import { ref, reactive, provide, inject } from 'vue'
import { createEndpoints } from '@/api/endpoints'
import { assertOk } from '@/utils/confirm'

const ORG_TREE_KEY = Symbol('orgTree')

export function createOrgTreeProvider(api) {
  const endpoints = createEndpoints(api)

  const tree = ref([])
  const treeLoaded = ref(false)
  const treeIncludesEmployees = ref(false)
  const treeLoading = ref(false)
  const treeError = ref(null)

  const departamentDetails = reactive({})
  const groupDetails = reactive({})
  const employeeDetails = reactive({})

  const departamentLoading = reactive({})
  const groupLoading = reactive({})
  const employeeLoading = reactive({})

  let inFlightTree = null
  const inFlightDepartament = {}
  const inFlightGroup = {}
  const inFlightEmployee = {}

  const invalidateTree = () => {
    tree.value = []
    treeLoaded.value = false
    treeIncludesEmployees.value = false
  }

  const invalidateDepartamentDetail = (id) => {
    delete departamentDetails[id]
  }

  const invalidateGroupDetail = (id) => {
    delete groupDetails[id]
  }

  const invalidateEmployeeDetail = (id) => {
    delete employeeDetails[id]
  }

  const ensureTree = async ({ includeEmployees = false, force = false } = {}) => {
    // Reuse the loaded tree; don't refetch if it already includes employees.
    if (
      !force
      && treeLoaded.value
      && (!includeEmployees || treeIncludesEmployees.value)
    ) {
      return tree.value
    }

    if (inFlightTree) {
      await inFlightTree
      if (
        !force
        && treeLoaded.value
        && (!includeEmployees || treeIncludesEmployees.value)
      ) {
        return tree.value
      }
    }

    // Once employees are cached, keep fetching them (never downgrade to the light tree).
    const fetchEmployees = includeEmployees || treeIncludesEmployees.value

    treeLoading.value = true
    treeError.value = null

    inFlightTree = endpoints
      .getDepartaments({ includeEmployees: fetchEmployees })
      .then((data) => {
        assertOk(data, 'Failed to load departments')
        tree.value = data.departaments || []
        treeLoaded.value = true
        treeIncludesEmployees.value = fetchEmployees
        return tree.value
      })
      .catch((err) => {
        treeError.value = err
        console.error('Error loading org tree:', err)
        throw err
      })
      .finally(() => {
        treeLoading.value = false
        inFlightTree = null
      })

    return inFlightTree
  }

  const loadDepartamentDetail = async (id, { force = false } = {}) => {
    if (!force && departamentDetails[id]) {
      return departamentDetails[id]
    }
    if (inFlightDepartament[id]) {
      return inFlightDepartament[id]
    }

    departamentLoading[id] = true
    inFlightDepartament[id] = endpoints
      .getDepartament(id)
      .then((data) => {
        assertOk(data, 'Failed to load department')
        departamentDetails[id] = data.departament
        return data.departament
      })
      .finally(() => {
        departamentLoading[id] = false
        delete inFlightDepartament[id]
      })

    return inFlightDepartament[id]
  }

  const loadGroupDetail = async (id, { force = false } = {}) => {
    if (!force && groupDetails[id]) {
      return groupDetails[id]
    }
    if (inFlightGroup[id]) {
      return inFlightGroup[id]
    }

    groupLoading[id] = true
    inFlightGroup[id] = endpoints
      .getGroup(id)
      .then((data) => {
        assertOk(data, 'Failed to load group')
        groupDetails[id] = data.group
        return data.group
      })
      .finally(() => {
        groupLoading[id] = false
        delete inFlightGroup[id]
      })

    return inFlightGroup[id]
  }

  const loadEmployeeDetail = async (id, { force = false } = {}) => {
    if (!force && employeeDetails[id]) {
      return employeeDetails[id]
    }
    if (inFlightEmployee[id]) {
      return inFlightEmployee[id]
    }

    employeeLoading[id] = true
    inFlightEmployee[id] = endpoints
      .getEmployee(id)
      .then((data) => {
        assertOk(data, 'Failed to load employee')
        employeeDetails[id] = data.employee
        return data.employee
      })
      .finally(() => {
        employeeLoading[id] = false
        delete inFlightEmployee[id]
      })

    return inFlightEmployee[id]
  }

  const createDepartament = async (payload) => {
    const result = await endpoints.createDepartament(payload)
    assertOk(result, 'Failed to create department')
    invalidateTree()
    await ensureTree({ includeEmployees: treeIncludesEmployees.value, force: true })
    return result
  }

  const createGroup = async (payload) => {
    const result = await endpoints.createGroup(payload)
    assertOk(result, 'Failed to create group')
    invalidateTree()
    if (payload.departament_id) {
      invalidateDepartamentDetail(payload.departament_id)
    }
    await ensureTree({ includeEmployees: treeIncludesEmployees.value, force: true })
    // Caller loads the detail if it needs it; don't double-fetch here.
    return result
  }

  const createEmployee = async (payload) => {
    const result = await endpoints.createEmployee(payload)
    assertOk(result, 'Failed to create employee')
    invalidateTree()
    if (payload.group_id) {
      invalidateGroupDetail(payload.group_id)
    }
    await ensureTree({ includeEmployees: treeIncludesEmployees.value, force: true })
    return result
  }

  const provider = {
    endpoints,
    tree,
    treeLoaded,
    treeIncludesEmployees,
    treeLoading,
    treeError,
    departamentDetails,
    groupDetails,
    employeeDetails,
    departamentLoading,
    groupLoading,
    employeeLoading,
    ensureTree,
    loadDepartamentDetail,
    loadGroupDetail,
    loadEmployeeDetail,
    invalidateTree,
    invalidateDepartamentDetail,
    invalidateGroupDetail,
    invalidateEmployeeDetail,
    createDepartament,
    createGroup,
    createEmployee,
  }

  provide(ORG_TREE_KEY, provider)
  return provider
}

export function useOrgTree() {
  const orgTree = inject(ORG_TREE_KEY)
  if (!orgTree) {
    throw new Error('useOrgTree must be used within AppProvider')
  }
  return orgTree
}
